import React, {
  useEffect,
  useState,
  useCallback,
  useContext,
  useMemo,
} from "react";
import ReactDOM from "react-dom";
import { Modal, Form, Button } from "react-bootstrap";
import axios from "axios";
import Select from "react-select";
import {
  customInvalidStyle,
  customValidatedStyle,
  customStyles,
} from "../../utils/react-select.custom-styles";
import API_ROUTE from "../../utils/apiRoute";
import { GlobalContext } from "../../GlobalState";

const toEmployeeOption = (employee) => ({
  value: employee._id,
  label: `${employee.name} (${employee.email.split("@")[0]})`,
});

const toSpaceManagerOption = (employee) => {
  const option = toEmployeeOption(employee);
  if (employee.role === "user") {
    option.label += " - sem permissão de gestor";
  }
  return option;
};

// The school lists only hold people of the selected school (and, for managers,
// with the "space-manager" role). A space's current manager or responsible can
// be outside them (no school, another school, role changed): keep them listed,
// otherwise the form hides them and saving silently removes them.
const withCurrentEmployees = (options, currentEmployees, toOption) => {
  const missing = currentEmployees
    .filter((employee) => !options.some((o) => o.value === employee._id))
    .map(toOption);

  return missing.length > 0 ? [...options, ...missing] : options;
};

const EditSpaceModal = ({
  isShowing,
  toggle,
  spaceId,
  setTitleText,
  toggleActionFeedback,
  setIsActionCompleted,
}) => {
  const [validated, setValidated] = useState(false);

  const [allTypesOptions, setAllTypesOptions] = useState([]);
  const [allResources, setAllResources] = useState([]);

  const [allSchoolsOptions, setAllSchoolsOptions] = useState([]);
  const [selectedSchoolOption, setSelectedSchoolOption] = useState(null);

  const [typeObj, setTypeObj] = useState(null);
  const [typeId, setTypeId] = useState(null);

  const [isAdmin, setIsAdmin] = useState(false);

  const [allSumariosSpaceNameOptions, setAllSumariosSpaceNameOptions] =
    useState([]);
  const [sumariosSpaces, setSumariosSpaces] = useState(null);
  const [sumariosSpaceNameId, setSumariosSpaceNameId] = useState([]);

  const [allSpaceManagers, setAllSpaceManagers] = useState(null);
  const [currentSpaceManagers, setCurrentSpaceManagers] = useState([]);
  const [selectedSpaceManager, setSelectedSpaceManager] = useState(null);

  const [selectedTechnicians, setSelectedTechnicians] = useState([]);

  const [spaceManagerId, setSpaceManagerId] = useState(null);

  const [allEmployees, setAllEmployees] = useState([]);
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [defaultResponsibleId, setDefaultResponsibleId] = useState(null);
  const [currentResponsible, setCurrentResponsible] = useState(null);

  const spaceManagerOptions = useMemo(
    () =>
      withCurrentEmployees(
        allSpaceManagers || [],
        currentSpaceManagers,
        toSpaceManagerOption
      ),
    [allSpaceManagers, currentSpaceManagers]
  );

  const employeeOptions = useMemo(
    () =>
      withCurrentEmployees(
        allEmployees,
        currentResponsible ? [currentResponsible] : [],
        toEmployeeOption
      ),
    [allEmployees, currentResponsible]
  );

  const [isSpaceManagerInputValid, setIsSpaceManagerInputValid] =
    useState("null");

  const [isActive, setIsActive] = useState(true);
  const [requireZoomLicense, setRequireZoomLicense] = useState(false);
  const [autoApproveReservations, setAutoApproveReservations] = useState(false);
  const [zoomIntegration, setZoomIntegration] = useState({
    isConnected: false,
    zoomEmail: "",
    zoomUserId: "",
    connectedAt: null,
  });

  const { selectedSchoolCode, setSelectedSchoolCode } =
    useContext(GlobalContext);

  const [spaceFormData, setSpaceFormData] = useState({
    spaceName: "",
    spaceNumber: "",
    spaceInitials: "",
    spaceDescription: "",
    spaceLocation: "",
    spacePhoneNumber: "",
    spaceNumberOfDoors: "",
    spaceCapacity: "",
    spaceCoordinates: "",
    spaceTemplate: "",
    spaceNotes: "",
  });

  const onHandleChange = useCallback((evt) => {
    const { value, name } = evt.currentTarget;

    setSpaceFormData((prevState) => {
      return { ...prevState, [name]: value };
    });
  }, []);

  const [selectedResourcesIds, setSelectedResourcesIds] = useState("");
  const [selectedResourcesObj, setSelectedResourcesObj] = useState("");

  const setDefaultTechnicians = (technicians) => {
    if (technicians && technicians.length > 0) {
      const defaultTechnicians = technicians.map((technician) => {
        return { label: technician.name, value: technician._id };
      });

      setSelectedTechnicians(defaultTechnicians);
    } else {
      setSelectedTechnicians([]);
    }
  };

  const reloadSpaceData = useCallback(async () => {
    if (!spaceId) return;

    try {
      const spaceResponse = await axios.get(`${API_ROUTE}space/${spaceId}/`, {
        withCredentials: true,
      });

      const spaceData = spaceResponse.data.data.spaceData;

      setSpaceFormData({
        spaceName: spaceData.name || "",
        spaceNumber: spaceData.number || "",
        spaceInitials: spaceData.initials || "",
        spaceDescription: spaceData.description || "",
        spaceLocation: spaceData.location || "",
        spacePhoneNumber: spaceData.phoneNumber || "",
        spaceNumberOfDoors: spaceData.numberOfDoors || "",
        spaceCapacity: spaceData.capacity || "",
        spaceCoordinates: spaceData.coordinates || "",
        spaceTemplate: spaceData.template || "",
        spaceNotes: spaceData.notes || "",
      });

      setSelectedResourcesIds(spaceData.resources || []);
      setSumariosSpaceNameId(spaceData.spaceCodesInSumarios || []);
      setTypeId(spaceData.typeId || null);
      setCurrentSpaceManagers(spaceData.managers || []);
      setSpaceManagerId(spaceData.spaceManagerId || null);
      setCurrentResponsible(spaceData.responsibleObj || null);
      setDefaultResponsibleId(spaceData.responsibleId);
      setDefaultTechnicians(spaceData.technicians || []);
      setIsActive(
        typeof spaceData.isActive === "boolean" ? spaceData.isActive : true
      );
      setRequireZoomLicense(
        typeof spaceData.requireZoomLicense === "boolean"
          ? spaceData.requireZoomLicense
          : false
      );
      setAutoApproveReservations(spaceData.autoApproveReservations === true);

      setZoomIntegration({
        isConnected: !!spaceData?.zoomIntegration?.isConnected,
        zoomEmail: spaceData?.zoomIntegration?.zoomEmail || "",
        zoomUserId: spaceData?.zoomIntegration?.zoomUserId || "",
        connectedAt: spaceData?.zoomIntegration?.connectedAt || null,
        needsReconnect: !!spaceData?.zoomIntegration?.needsReconnect,
      });

      const schoolsResponse = await axios.get(`${API_ROUTE}school`, {
        withCredentials: true,
      });

      setAllSchoolsOptions(
        schoolsResponse.data.data.allSchools.map((school) => ({
          value: school.code,
          label: school.name,
        }))
      );
    } catch (err) {
      console.error(err);
    }
  }, [spaceId]);

  useEffect(() => {
    if (isShowing && spaceId) {
      reloadSpaceData();
    }
  }, [spaceId, isShowing, reloadSpaceData]);

  useEffect(() => {
    if (spaceManagerId && spaceManagerOptions.length > 0) {
      const defaultSpaceManager = spaceManagerOptions.filter((spaceManager) =>
        spaceManagerId.some(
          (defaultManager) => defaultManager === spaceManager.value
        )
      );
      setSelectedSpaceManager(defaultSpaceManager);
    }
  }, [spaceManagerId, spaceManagerOptions]);

  useEffect(() => {
    if (typeId && allTypesOptions.length > 0) {
      const defaultTypeValue = allTypesOptions.find(
        (type) => type.value === typeId
      );
      setTypeObj(defaultTypeValue || null);
      setTypeId(typeId);
    }
  }, [typeId, allTypesOptions]);

  useEffect(() => {
    if (defaultResponsibleId && employeeOptions.length > 0) {
      const defaultEmployeeResponsible = employeeOptions.find(
        (employee) => employee.value === defaultResponsibleId
      );
      setSelectedEmployee(defaultEmployeeResponsible || null);
    }
    if (defaultResponsibleId === undefined) {
      if (employeeOptions.length > 0) {
        setSelectedEmployee(employeeOptions[0]);
      }
    }
  }, [defaultResponsibleId, employeeOptions]);

  useEffect(() => {
    const fetchSumariosSpaces = async () => {
      if (sumariosSpaceNameId && sumariosSpaceNameId.length > 0) {
        try {
          const defaultSumariosOption = await Promise.all(
            sumariosSpaceNameId.map(async (ssid) => {
              const spaceFromSumariosResponse = await axios.get(
                `${API_ROUTE}space/sumarios/school/${ssid.schoolCode}`,
                {
                  withCredentials: true,
                }
              );

              if (!spaceFromSumariosResponse?.data?.data?.sumariosSpaces)
                return null;

              const spaceData =
                spaceFromSumariosResponse?.data?.data?.sumariosSpaces.map(
                  (sfs) => {
                    return {
                      value: {
                        spaceId: sfs.id,
                        spaceCode: sfs.codSala,
                        schoolCode: sfs.codEscola,
                      },
                      label: `${sfs.nome} - ${sfs.abrevEscola}`,
                    };
                  }
                );

              const sumariosOption = [];
              sumariosOption.push(
                spaceData.filter(
                  (sp) => ssid.spaceCode === sp.value.spaceCode
                )[0]
              );
              return sumariosOption;
            })
          );

          setSumariosSpaces(defaultSumariosOption.filter(Boolean));
        } catch (error) {
          console.error("Error fetching sumarios space names:", error);
        }
      } else {
        setSumariosSpaces([]);
      }
    };

    fetchSumariosSpaces();
  }, [sumariosSpaceNameId]);

  const handleEmployeeChange = (selected) => {
    setSelectedEmployee(selected);
  };

  const handleChangeTechnicians = (selected) => {
    setSelectedTechnicians(selected);
  };

  const handleChangeTypeId = (selected) => {
    setTypeId(selected?.value || null);
    setTypeObj(selected || null);
  };

  const handleChangeResources = (selected) => {
    setSelectedResourcesObj(selected);
  };

  const handleSpaceManagerChange = (selected) => {
    if (selected && isSpaceManagerInputValid === "false") {
      setIsSpaceManagerInputValid("true");
    }
    setSelectedSpaceManager(selected);
  };

  const handleChangeSumarios = (selected) => {
    setSumariosSpaces(selected);
  };

  const buildDataToSend = () => {
    let resources = [];
    if (selectedResourcesObj && selectedResourcesObj.length > 0) {
      resources = selectedResourcesObj.map((resource) => resource.value);
    }

    let technicians = [];
    if (selectedTechnicians && selectedTechnicians.length > 0) {
      technicians = selectedTechnicians.map((tech) => tech.value);
    }

    let sumariosSpacesFormatted = [];
    if (sumariosSpaces && sumariosSpaces.length > 0) {
      sumariosSpacesFormatted = sumariosSpaces
        .flat()
        .map((sumariosSpace) => sumariosSpace.value);
    }

    const spaceManagersFormatted =
      selectedSpaceManager && selectedSpaceManager.length > 0
        ? selectedSpaceManager.map((spaceManager) => spaceManager.value)
        : [];

    return {
      name: spaceFormData.spaceName,
      number: spaceFormData.spaceNumber,
      initials: spaceFormData.spaceInitials,
      description: spaceFormData.spaceDescription,
      location: spaceFormData.spaceLocation,
      phoneNumber: spaceFormData.spacePhoneNumber,
      numberOfDoors: spaceFormData.spaceNumberOfDoors,
      capacity: spaceFormData.spaceCapacity,
      coordinates: spaceFormData.spaceCoordinates,
      template: spaceFormData.spaceTemplate,
      notes: spaceFormData.spaceNotes,
      typeId: typeId,
      spaceCodesInSumarios: sumariosSpacesFormatted,
      resources,
      spaceManagerId: spaceManagersFormatted,
      responsibleId: selectedEmployee?.value,
      technicians,
      isActive: isActive,
      zoomIntegration,
      requireZoomLicense: requireZoomLicense,
      autoApproveReservations: autoApproveReservations,
    };
  };

  const persistCurrentForm = async () => {
    const dataToSend = buildDataToSend();

    await axios.put(`${API_ROUTE}space/${spaceId}`, dataToSend, {
      withCredentials: true,
    });
  };

  useEffect(() => {
    const backendOrigin = new URL(API_ROUTE).origin;

    const handleZoomPopupMessage = async (event) => {
      if (event.origin !== backendOrigin) return;

      if (
        event.data?.type === "ZOOM_CONNECTED" &&
        String(event.data?.spaceId) === String(spaceId)
      ) {
        await reloadSpaceData();
        setTitleText("Conta Zoom ligada com sucesso!");
        setIsActionCompleted(true);
        toggleActionFeedback();
      }

      if (
        event.data?.type === "ZOOM_CONNECT_ERROR" &&
        (String(event.data?.spaceId) === String(spaceId) ||
          event.data?.spaceId === "unknown")
      ) {
        await reloadSpaceData();
        setTitleText(
          event.data?.message || "Não foi possível ligar a conta Zoom."
        );
        setIsActionCompleted(false);
        toggleActionFeedback();
      }
    };

    window.addEventListener("message", handleZoomPopupMessage);

    return () => {
      window.removeEventListener("message", handleZoomPopupMessage);
    };
  }, [
    spaceId,
    reloadSpaceData,
    setTitleText,
    setIsActionCompleted,
    toggleActionFeedback,
  ]);

  const handleConnectZoom = async (event) => {
    event.preventDefault();

    try {
      await persistCurrentForm();

      const response = await axios.get(
        `${API_ROUTE}space/${spaceId}/zoom/connect`,
        {
          withCredentials: true,
        }
      );

      const authUrl = response?.data?.data?.authUrl;

      if (!authUrl) {
        throw new Error(
          "Não foi possível obter o URL de autenticação do Zoom."
        );
      }

      const popup = window.open(
        authUrl,
        "zoom-oauth-popup",
        "width=640,height=760,menubar=no,toolbar=no,location=yes,resizable=yes,scrollbars=yes,status=no"
      );

      if (!popup) {
        setTitleText(
          "O popup foi bloqueado pelo navegador. Permita popups para continuar."
        );
        setIsActionCompleted(false);
        toggleActionFeedback();
      }
    } catch (err) {
      setTitleText(
        err?.response?.data?.message ||
          err?.message ||
          "Não foi possível iniciar a ligação da conta Zoom."
      );
      setIsActionCompleted(false);
      toggleActionFeedback();
    }
  };

  const handleDisconnectZoom = async (event) => {
    event.preventDefault();

    try {
      await axios.delete(`${API_ROUTE}space/${spaceId}/zoom`, {
        withCredentials: true,
      });

      setZoomIntegration({
        isConnected: false,
        zoomEmail: "",
        zoomUserId: "",
        connectedAt: null,
      });

      setTitleText("Conta Zoom desligada com sucesso!");
      setIsActionCompleted(true);
      toggleActionFeedback();
    } catch (err) {
      setTitleText(
        err?.response?.data?.message ||
          "Não foi possível desligar a conta Zoom."
      );
      setIsActionCompleted(false);
      toggleActionFeedback();
    }
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    const form = event.currentTarget;

    if (!selectedSpaceManager || selectedSpaceManager.length === 0) {
      setIsSpaceManagerInputValid("false");
    }

    if (
      selectedSpaceManager !== null &&
      selectedSpaceManager.length !== 0 &&
      form.checkValidity() === true
    ) {
      const dataToSend = buildDataToSend();

      (async () => {
        await axios
          .put(`${API_ROUTE}space/${spaceId}`, dataToSend, {
            withCredentials: true,
          })
          .then(() => {
            setTitleText("Espaço alterado com sucesso!");
            setIsActionCompleted(true);
            toggleActionFeedback();
            toggle();
          })
          .catch((err) => {
            if (err.response?.data?.message) {
              setTitleText(err.response.data.message);
            } else {
              setTitleText(
                "Algo inesperado aconteceu, tente novamente mais tarde!"
              );
            }
            setIsActionCompleted(false);
            toggleActionFeedback();
            toggle();
          });
      })();
    } else {
      event.stopPropagation();
    }

    setValidated(true);
  };

  useEffect(() => {
    (async () => {
      if (selectedSchoolCode) {
        await axios
          .get(`${API_ROUTE}type/school/${selectedSchoolCode}`, {
            withCredentials: true,
          })
          .then((res) => {
            const allTypes = res.data.data.allTypesBySchool;

            const tmpAllTypesOptions = allTypes.map((type) => {
              return {
                value: type._id,
                label: type.name,
              };
            });
            setAllTypesOptions(tmpAllTypesOptions);
          });

        await axios
          .get(`${API_ROUTE}resource/school/${selectedSchoolCode}`, {
            withCredentials: true,
          })
          .then((res) => {
            const tmpAllResources = res.data.data.allResourcesBySchool.map(
              (resource) => {
                return {
                  value: resource._id,
                  label: resource.name,
                  description: resource.description,
                };
              }
            );
            if (tmpAllResources.length > 0) {
              setAllResources(tmpAllResources);
            }
          });

        await axios
          .get(`${API_ROUTE}employee/getAll/school/${selectedSchoolCode}`, {
            withCredentials: true,
          })
          .then((res) => {
            const allEmployeesData = res.data.data.employees;

            setAllEmployees(allEmployeesData.map(toEmployeeOption));

            const spaceManagers = allEmployeesData
              .filter((employee) => employee.role === "space-manager")
              .map(toEmployeeOption);

            setAllSpaceManagers(spaceManagers);
          });
      }

      await axios
        .get(`${API_ROUTE}employee`, {
          withCredentials: true,
        })
        .then((res) => {
          if (
            res.data.user.role === "system-manager" ||
            res.data.user.role === "system-master-manager"
          ) {
            setIsAdmin(true);
          }
        });
    })();
  }, [selectedSchoolCode, setSelectedSchoolCode]);

  useEffect(() => {
    (async () => {
      if (selectedSchoolOption) {
        await axios
          .get(
            `${API_ROUTE}space/sumarios/school/${selectedSchoolOption?.value}`,
            {
              withCredentials: true,
            }
          )
          .then((res) => {
            const sumariosSpaceData = res.data.data.sumariosSpaces;

            const allSumariosSpacesFormatted = sumariosSpaceData.map(
              (sumariosSpace) => {
                return {
                  value: {
                    spaceId: sumariosSpace.id,
                    spaceCode: sumariosSpace.codSala,
                    schoolCode: sumariosSpace.codEscola,
                  },
                  label: `${sumariosSpace.nome} - ${sumariosSpace.abrevEscola}`,
                };
              }
            );

            setAllSumariosSpaceNameOptions(allSumariosSpacesFormatted);
          });
      }
    })();
  }, [selectedSchoolOption]);

  useEffect(() => {
    if (selectedResourcesIds.length === 0) {
      setSelectedResourcesObj(null);
    }
    if (selectedResourcesIds.length > 0 && allResources.length > 0) {
      const defaultValue = allResources.filter((val) => {
        return selectedResourcesIds.includes(val.value);
      });
      setSelectedResourcesObj(defaultValue);
    }
  }, [selectedResourcesIds, allResources]);

  const handleSchoolChange = (selectedOption) => {
    setSelectedSchoolOption(selectedOption);
    setAllSumariosSpaceNameOptions([]);
  };

  return isShowing
    ? ReactDOM.createPortal(
        <React.Fragment>
          <Modal
            show={isShowing}
            onHide={() => {
              setValidated(false);
              toggle();
            }}
          >
            <Form noValidate validated={validated} onSubmit={handleSubmit}>
              <Modal.Header closeButton>
                <Modal.Title>Editar Espaço</Modal.Title>
              </Modal.Header>

              <Modal.Body>
                <Form.Group controlId="validationCustomName">
                  <Form.Label>Nome</Form.Label>
                  <Form.Control
                    required
                    type="text"
                    placeholder="Nome do Espaço"
                    value={spaceFormData.spaceName}
                    onChange={onHandleChange}
                    name="spaceName"
                  />
                  <Form.Control.Feedback type="invalid">
                    O espaço precisa de um nome!
                  </Form.Control.Feedback>
                </Form.Group>

                <Form.Group controlId="validationCustomFloor">
                  <Form.Label>Tipo</Form.Label>
                  <Select
                    placeholder="Selecione ou digite o tipo do espaço"
                    options={allTypesOptions}
                    value={typeObj}
                    onChange={handleChangeTypeId}
                  />
                </Form.Group>

                {isAdmin ? (
                  <Form.Group controlId="validationCustomFloor">
                    <Form.Label>Gestor do Espaço</Form.Label>

                    <Select
                      styles={
                        isSpaceManagerInputValid === "true"
                          ? customValidatedStyle
                          : isSpaceManagerInputValid === "false"
                          ? customInvalidStyle
                          : customStyles
                      }
                      isMulti
                      placeholder="Selecione ou digite o nome do gestor do espaço"
                      options={spaceManagerOptions}
                      value={selectedSpaceManager}
                      onChange={handleSpaceManagerChange}
                    />
                  </Form.Group>
                ) : null}

                <Form.Group controlId="validationCustomFloor">
                  <Form.Label>Responsável</Form.Label>
                  <Select
                    placeholder="Selecione ou digite o nome do responsável"
                    options={employeeOptions}
                    value={selectedEmployee}
                    onChange={handleEmployeeChange}
                  />
                </Form.Group>

                <Form.Group controlId="validationCustomFloor">
                  <Form.Label>Técnicos</Form.Label>
                  <Select
                    isMulti
                    placeholder="Selecione ou digite o(s) nome(s) do(s) técnico(s)"
                    options={allEmployees}
                    onChange={handleChangeTechnicians}
                    value={selectedTechnicians}
                  />
                </Form.Group>

                <Form.Group controlId="validationCustomSchool">
                  <Form.Label>
                    Nome do Espaço nos Sumários (se houver)
                  </Form.Label>
                  <Select
                    isClearable
                    placeholder="Selecione a escola"
                    options={allSchoolsOptions}
                    value={selectedSchoolOption}
                    onChange={handleSchoolChange}
                  />

                  <Form.Group
                    controlId="validationCustomFloor"
                    className="mt-3"
                  >
                    <Select
                      isMulti
                      isClearable
                      placeholder="Selecione ou digite o nome do espaço nos sumários"
                      options={allSumariosSpaceNameOptions}
                      value={sumariosSpaces?.flat()}
                      onChange={handleChangeSumarios}
                      isDisabled={!selectedSchoolOption}
                    />
                  </Form.Group>
                </Form.Group>

                <Form.Group controlId="validationCustomName">
                  <Form.Label>Número</Form.Label>
                  <Form.Control
                    required
                    type="number"
                    placeholder="Número do Espaço"
                    name="spaceNumber"
                    onChange={onHandleChange}
                    value={spaceFormData.spaceNumber}
                  />
                  <Form.Control.Feedback type="invalid">
                    O espaço precisa de um número!
                  </Form.Control.Feedback>
                </Form.Group>

                <Form.Group controlId="validationCustomName">
                  <Form.Label>Abreviatura</Form.Label>
                  <Form.Control
                    required
                    type="text"
                    placeholder="Abreviatura do Espaço"
                    name="spaceInitials"
                    onChange={onHandleChange}
                    value={spaceFormData.spaceInitials}
                  />
                  <Form.Control.Feedback type="invalid">
                    O espaço precisa de uma abreviatura!
                  </Form.Control.Feedback>
                </Form.Group>

                <Form.Group controlId="validationCustomName">
                  <Form.Label>Descrição</Form.Label>
                  <Form.Control
                    required
                    type="text"
                    placeholder="Descrição do Espaço"
                    name="spaceDescription"
                    onChange={onHandleChange}
                    value={spaceFormData.spaceDescription}
                  />
                  <Form.Control.Feedback type="invalid">
                    O espaço precisa de uma descrição!
                  </Form.Control.Feedback>
                </Form.Group>

                <Form.Group controlId="validationCustomName">
                  <Form.Label>Localização</Form.Label>
                  <Form.Control
                    required
                    type="text"
                    placeholder="Localização do Espaço"
                    name="spaceLocation"
                    onChange={onHandleChange}
                    value={spaceFormData.spaceLocation}
                  />
                  <Form.Control.Feedback type="invalid">
                    O espaço precisa de uma Localização!
                  </Form.Control.Feedback>
                </Form.Group>

                <Form.Group controlId="validationCustomName">
                  <Form.Label>Extensão Telefónica</Form.Label>
                  <Form.Control
                    type="number"
                    placeholder="Extensão Telefónica"
                    name="spacePhoneNumber"
                    onChange={onHandleChange}
                    value={spaceFormData.spacePhoneNumber}
                  />
                </Form.Group>

                <Form.Group controlId="validationCustomName">
                  <Form.Label>Número de Portas</Form.Label>
                  <Form.Control
                    required
                    type="number"
                    placeholder="Número de Portas"
                    name="spaceNumberOfDoors"
                    onChange={onHandleChange}
                    value={spaceFormData.spaceNumberOfDoors}
                  />
                  <Form.Control.Feedback type="invalid">
                    O espaço precisa de um número de portas!
                  </Form.Control.Feedback>
                </Form.Group>

                <Form.Group controlId="validationCustomName">
                  <Form.Label>Capacidade</Form.Label>
                  <Form.Control
                    required
                    type="number"
                    placeholder="Capacidade do Espaço"
                    name="spaceCapacity"
                    onChange={onHandleChange}
                    value={spaceFormData.spaceCapacity}
                  />
                  <Form.Control.Feedback type="invalid">
                    O espaço precisa de uma Capacidade!
                  </Form.Control.Feedback>
                </Form.Group>

                <Form.Group controlId="validationCustomFloor">
                  <Form.Label>Recursos</Form.Label>
                  <Select
                    isMulti
                    placeholder="Selecione ou digite o tipo do espaço"
                    options={allResources}
                    onChange={handleChangeResources}
                    value={selectedResourcesObj}
                  />
                </Form.Group>

                <Form.Group controlId="validationCustomCoordinates">
                  <Form.Label>Coordernadas</Form.Label>
                  <Form.Control
                    type="text"
                    placeholder="Coordenadas do Espaço"
                    required
                    name="spaceCoordinates"
                    onChange={onHandleChange}
                    value={spaceFormData.spaceCoordinates}
                  />
                  <Form.Control.Feedback type="invalid">
                    O espaço precisa de Coordernadas!
                  </Form.Control.Feedback>
                </Form.Group>

                <Form.Group controlId="validationCustomTemplate">
                  <Form.Label>Template</Form.Label>
                  <Form.Control
                    as="textarea"
                    aria-label="With textarea"
                    placeholder="Template da Descrição de Reserva"
                    name="spaceTemplate"
                    onChange={onHandleChange}
                    value={spaceFormData.spaceTemplate}
                  />
                </Form.Group>

                <Form.Group controlId="validationCustomNotes">
                  <Form.Label>Notas</Form.Label>
                  <Form.Control
                    as="textarea"
                    aria-label="With textarea"
                    placeholder="Notas sobre o Espaço"
                    name="spaceNotes"
                    onChange={onHandleChange}
                    value={spaceFormData.spaceNotes}
                  />
                </Form.Group>

                <Form.Group
                  controlId="validationRequireZoomLicense"
                  className="mt-3"
                >
                  <Form.Check
                    type="checkbox"
                    label="Requer Licença Zoom?"
                    name="requireZoomLicense"
                    checked={requireZoomLicense}
                    onChange={(e) => setRequireZoomLicense(e.target.checked)}
                  />
                </Form.Group>

                <Form.Group
                  controlId="validationAutoApproveReservations"
                  className="mt-3"
                >
                  <Form.Check
                    type="checkbox"
                    label="Aprovação automática das reservas"
                    name="autoApproveReservations"
                    checked={autoApproveReservations}
                    disabled={!isAdmin}
                    onChange={(e) =>
                      setAutoApproveReservations(e.target.checked)
                    }
                  />
                  <Form.Text className="text-muted">
                    Os pedidos ficam logo reservados, sem esperar pelo gestor,
                    se o horário estiver livre e, nos espaços Zoom, se a sessão
                    Zoom for criada. Caso contrário, ficam em análise.
                    {isAdmin
                      ? ""
                      : " Só um administrador pode alterar esta opção."}
                  </Form.Text>
                </Form.Group>

                {typeObj?.label === "Sala Virtual Zoom" ||
                requireZoomLicense ? (
                  <Form.Group controlId="validationCustomZoom" className="mt-3">
                    <Form.Label>Integração Zoom</Form.Label>

                    <div className="d-flex flex-column">
                      <div className="mb-2">
                        <strong>Estado:</strong>{" "}
                        {!zoomIntegration.isConnected
                          ? "Não ligado"
                          : zoomIntegration.needsReconnect
                          ? "Precisa de religar (o token expirou ou foi revogado)"
                          : "Ligado"}
                      </div>

                      {zoomIntegration.isConnected ? (
                        <>
                          <div className="mb-2">
                            <strong>Conta:</strong>{" "}
                            {zoomIntegration.zoomEmail || "-"}
                          </div>
                          <div className="d-flex">
                            <Button
                              variant="outline-secondary"
                              className="mr-2"
                              onClick={handleConnectZoom}
                            >
                              Reconfigurar Zoom
                            </Button>
                            <Button
                              variant="outline-danger"
                              onClick={handleDisconnectZoom}
                            >
                              Desligar Zoom
                            </Button>
                          </div>
                        </>
                      ) : (
                        <Button
                          variant="outline-primary"
                          onClick={handleConnectZoom}
                        >
                          Ligar Conta Zoom
                        </Button>
                      )}
                    </div>
                  </Form.Group>
                ) : null}

                <Form.Group
                  controlId="validationCustomIsActive"
                  className="mt-3"
                >
                  <Form.Check
                    type="checkbox"
                    label="Ativo"
                    name="isActive"
                    checked={isActive}
                    onChange={(e) => setIsActive(e.target.checked)}
                  />
                </Form.Group>
              </Modal.Body>

              <Modal.Footer>
                <Button variant="danger" onClick={toggle}>
                  Cancelar
                </Button>
                <Button type="submit" variant="success">
                  Confirmar
                </Button>
              </Modal.Footer>
            </Form>
          </Modal>
        </React.Fragment>,
        document.body
      )
    : null;
};

export default EditSpaceModal;
